// src/ui/screens/chats/widgets/SelectionTopBar.jsx

import React, { useState, useRef, useEffect } from 'react';

const COLORS = {
  orange: '#FF8C08',
  card: '#23262C',
  cardLight: '#2C2F36',
  red: '#FF3B30',
};

// ── Vertical Divider ────────────────────────────────────────────────────────

const VDivider = () => {
  return (
    <div className="w-px h-5 bg-white/12 flex-shrink-0" />
  );
};

// ── Action Cell ─────────────────────────────────────────────────────────────

const ActionCell = ({ child, onTap, tooltip = '' }) => {
  return (
    <div className="flex-1 flex items-center justify-center h-11 relative group">
      <button
        onClick={onTap}
        className="w-full h-full flex items-center justify-center hover:bg-white/5 transition-colors"
        title={tooltip}
      >
        {child}
      </button>
      {tooltip && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"
             style={{ backgroundColor: COLORS.cardLight }}>
          {tooltip}
        </div>
      )}
    </div>
  );
};

// ── More Cell ───────────────────────────────────────────────────────────────

const MoreCell = ({ onMarkAsRead }) => {
  const [showMenu, setShowMenu] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const handleToggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleMarkAsRead = () => {
    setShowMenu(false);
    if (onMarkAsRead) onMarkAsRead();
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex-1 flex items-center justify-center h-11 relative">
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="w-full h-full flex items-center justify-center hover:bg-white/5 transition-colors"
      >
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute top-10 right-0 min-w-[160px] rounded-xl shadow-lg py-1 z-50"
          style={{ backgroundColor: COLORS.cardLight }}
        >
          <button
            onClick={handleMarkAsRead}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
          >
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 15l2 2 4-4" />
            </svg>
            <span className="text-white/90 text-sm font-medium">Mark as read</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ── SelectionTopBar ──────────────────────────────────────────────────────────

export const SelectionTopBar = ({
  selectedCount,
  onClose,
  onMarkAsRead,
  onPin,
  isPinned = false,
  onBlock,
  isBlocked = false,
  onMute,
  isMuted = false,
  onArchive,
  isArchived = false,
  onDelete,
}) => {
  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 px-4 pt-safe-top"
      style={{ paddingTop: 8 }}
    >
      <div
        className="h-11 rounded-full flex items-center overflow-hidden"
        style={{
          backgroundColor: 'rgba(35,38,44,0.3)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid #23262C',
        }}
      >
        {/* Close */}
        <ActionCell
          onTap={onClose}
          child={
            <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          }
        />

        {/* Count */}
        <div className="px-1 flex-shrink-0">
          <span className="text-filatom-primary text-sm font-semibold">
            {selectedCount}
          </span>
        </div>

        <VDivider />

        {/* Pin */}
        <ActionCell
          onTap={onPin}
          tooltip={isPinned ? 'Unpin' : 'Pin'}
          child={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isPinned ? COLORS.orange : '#FFFFFF' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isPinned ? "M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9z" : "M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9z"} />
            </svg>
          }
        />

        <VDivider />

        {/* Block */}
        <ActionCell
          onTap={onBlock}
          tooltip={isBlocked ? 'Unblock' : 'Block'}
          child={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isBlocked ? '#4CAF50' : '#FFFFFF' }}>
              {isBlocked ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              )}
            </svg>
          }
        />

        <VDivider />

        {/* Mute */}
        <ActionCell
          onTap={onMute}
          tooltip={isMuted ? 'Unmute' : 'Mute'}
          child={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isMuted ? COLORS.orange : '#FFFFFF' }}>
              {isMuted ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              )}
            </svg>
          }
        />

        <VDivider />

        {/* Archive */}
        <ActionCell
          onTap={onArchive}
          tooltip={isArchived ? 'Unarchive' : 'Archive'}
          child={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isArchived ? COLORS.orange : '#FFFFFF' }}>
              {isArchived ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              )}
            </svg>
          }
        />

        <VDivider />

        {/* Delete */}
        <ActionCell
          onTap={onDelete}
          tooltip="Delete"
          child={
            <svg className="w-5 h-5" fill="none" stroke={COLORS.red} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          }
        />

        <VDivider />

        {/* More */}
        <MoreCell onMarkAsRead={onMarkAsRead} />
      </div>
    </div>
  );
};

export default SelectionTopBar;